import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, BarChart3, Clock, Database, Shield, TrendingUp, Users, XCircle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const WhyCRMDataIsBroken = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Why Your CRM Data Is Broken (And What Actually Fixes It)",
      "description": "The uncomfortable truth about CRM data quality: 91% of CRM data is incomplete, 70% decays annually, and your team wastes 5+ hours/week on manual entry. Here's the fix.",
      "url": "https://noq5.ai/why-crm-data-is-broken",
      "author": { "@type": "Organization", "name": "NoQ5" },
      "publisher": { "@type": "Organization", "name": "NoQ5", "url": "https://noq5.ai" },
      "datePublished": "2026-04-03",
      "dateModified": "2026-04-03"
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://noq5.ai" },
        { "@type": "ListItem", "position": 2, "name": "Why CRM Data Is Broken", "item": "https://noq5.ai/why-crm-data-is-broken" }
      ]
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="Why Your CRM Data Is Broken (And What Fixes It) | NoQ5"
        description="91% of CRM data is incomplete. 70% decays every year. Your reps spend 5+ hours/week on data entry nobody trusts. Here's why — and the AI agent that actually fixes it."
        keywords="CRM data quality, CRM data broken, bad CRM data, CRM data problems, CRM data accuracy, dirty CRM data, CRM data decay, CRM data entry problems, salesforce data quality, hubspot data quality, CRM data cleaning, fix CRM data, CRM data issues"
        canonicalUrl="https://noq5.ai/why-crm-data-is-broken"
        jsonLd={jsonLd}
      />

      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-destructive/5" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-destructive/10 text-destructive text-sm font-medium mb-6">
              <AlertTriangle className="h-4 w-4" />
              <span>The Uncomfortable Truth</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              Your CRM Data Is <span className="text-destructive">Broken</span>.
              <br />Everyone Knows It. Nobody Fixes It.
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl">
              Your CRM is full of garbage data. Your team spends hours updating it. And nobody trusts it anyway. Here's why — and the only thing that actually works.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {[
              { value: "91%", label: "of CRM data is incomplete or stale", source: "Salesforce Research" },
              { value: "70%", label: "of CRM data decays every year", source: "Marketing Sherpa" },
              { value: "5.5h", label: "per rep per week on manual entry", source: "Forrester" },
              { value: "$12M", label: "avg annual cost of bad CRM data (enterprise)", source: "Gartner" },
            ].map((stat) => (
              <div key={stat.label} className="text-center p-4 bg-destructive/5 rounded-xl border border-destructive/20">
                <div className="text-2xl md:text-3xl font-bold text-destructive mb-1">{stat.value}</div>
                <div className="text-xs text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="prose prose-lg max-w-none text-muted-foreground space-y-8">
            <h2 className="text-3xl font-bold text-foreground">The 5 Reasons Your CRM Data Is Garbage</h2>
            
            <div className="bg-card border border-border rounded-xl p-6 not-prose">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/10 text-destructive flex items-center justify-center font-bold">1</div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Reps Don't Update It — Because It's Painful</h3>
                  <p className="text-muted-foreground">Every CRM update is friction. After a 45-minute discovery call, the last thing any rep wants to do is spend 15 minutes filling in 20 fields. So they don't. Or they do the minimum. Or they batch it on Friday — with half the context lost.</p>
                  <p className="text-muted-foreground mt-2">This isn't a training problem. It's a design problem. <strong>CRMs were built as databases, not workflows.</strong> They expect humans to be data entry clerks. Humans aren't built for that.</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6 not-prose">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/10 text-destructive flex items-center justify-center font-bold">2</div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Data Decays Faster Than You Can Fix It</h3>
                  <p className="text-muted-foreground">People change jobs. Companies pivot. Budgets shift. Phone numbers change. 70% of your CRM data becomes stale within 12 months — and that's being generous. By the time your SDR calls that "Director of Sales Ops," they've been VP of Revenue at a different company for 6 months.</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6 not-prose">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/10 text-destructive flex items-center justify-center font-bold">3</div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Activities Happen Everywhere — CRM Captures Almost None</h3>
                  <p className="text-muted-foreground">Your rep had a critical Slack conversation about a deal. They exchanged 12 emails with a champion. They had a quick LinkedIn message exchange with the CFO. None of that is in Salesforce. The CRM only knows what someone manually types into it — which is maybe 30% of what actually happened.</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6 not-prose">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/10 text-destructive flex items-center justify-center font-bold">4</div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Forecasts Built on Bad Data Are Just Fiction</h3>
                  <p className="text-muted-foreground">Your CRO looks at the pipeline and sees $8M in "Commit." But half those deals haven't had buyer engagement in 3 weeks. Two have champions who changed jobs. One has a close date that's been pushed 4 times. The forecast isn't a forecast — it's a wish list built on stale data and rep optimism.</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6 not-prose">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/10 text-destructive flex items-center justify-center font-bold">5</div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-2">More Tools Make It Worse, Not Better</h3>
                  <p className="text-muted-foreground">Most companies react by adding more tools: enrichment databases, data cleaning services, dashboard layers. Now you have 6 tools that all disagree on which data is "right," syncs that break, and a RevOps team spending 40% of their time reconciling data between systems. The problem isn't lack of tools. It's that <strong>humans shouldn't be responsible for CRM data at all.</strong></p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-foreground mt-16">What Doesn't Work</h2>
            
            <div className="space-y-4 not-prose">
              {[
                { title: "CRM training & enforcement", reason: "You can train reps to update CRM. They'll do it for 2 weeks. Then they stop. Every sales leader has tried this. It doesn't stick because the fundamental problem is that data entry is anti-human." },
                { title: "Workflow automation (Zapier/Make)", reason: "Rules-based automation can move data between systems, but it can't understand that a meeting discussion revealed a competitor threat, or that the buyer's tone shifted from interested to non-committal. It's plumbing, not intelligence." },
                { title: "Data enrichment tools (Apollo/ZoomInfo)", reason: "These fill in contact data — which helps — but they don't log activities, update deal stages, capture meeting context, or generate forecasts. They solve 10% of the problem." },
                { title: "Hiring more RevOps / sales ops", reason: "You can throw people at the problem. But even a great RevOps team can only clean data retroactively. They can't capture what happened in a meeting in real-time. And they cost $100K+ each." },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border border-border">
                  <XCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-foreground">{item.title}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{item.reason}</p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="text-3xl font-bold text-foreground mt-16">The Only Thing That Actually Works</h2>
            
            <p>
              The answer isn't better training, more tools, or bigger RevOps teams. The answer is removing humans from the data entry loop entirely.
            </p>
            <p>
              A <strong><Link to="/crm-agent" className="text-primary hover:underline">CRM AI agent</Link></strong> doesn't suggest updates. It doesn't send reminders. It doesn't create dashboards. It <em>does the work</em>:
            </p>

            <div className="space-y-3 not-prose">
              {[
                "Listens to every call, reads every email, monitors every Slack thread",
                "Maps each interaction to the right contact, company, and deal",
                "Extracts buying signals, objections, next steps, and competitor mentions",
                "Updates 40+ CRM fields automatically — deal stage, amount, close date, notes, tasks",
                "Enriches contacts with job title, company data, tech stack from 15+ sources",
                "Generates evidence-based forecasts from actual engagement, not rep guesses",
                "Detects at-risk deals within 48 hours — not 3 weeks later"
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground">{item}</p>
                </div>
              ))}
            </div>

            <p className="mt-8">
              This isn't a copilot. It's not suggestions. It's not a dashboard.
            </p>
            <p className="text-xl font-semibold text-foreground">
              It's execution. Your CRM runs itself.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Stop Fighting Your CRM. Let It Run Itself.
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            NoQ5 is the CRM AI agent that eliminates manual data entry, fixes data quality at the source, and makes your pipeline actually trustworthy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/trial">
              <Button size="lg" className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">
                Deploy Your CRM Agent <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/crm-agent">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-border text-foreground hover:bg-accent">
                How CRM Agents Work
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-xl font-semibold text-foreground mb-6">Related</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/crm-agent" className="text-primary hover:underline text-sm">CRM AI Agent</Link>
            <Link to="/crm-data-entry-automation" className="text-primary hover:underline text-sm">CRM Data Entry Automation</Link>
            <Link to="/crm-enrichment-agent" className="text-primary hover:underline text-sm">CRM Enrichment Agent</Link>
            <Link to="/blog/why-sales-reps-hate-crms" className="text-primary hover:underline text-sm">Why Sales Reps Hate CRMs</Link>
            <Link to="/pipeline-management-ai" className="text-primary hover:underline text-sm">Pipeline Management AI</Link>
            <Link to="/blog/crm-automation-guide" className="text-primary hover:underline text-sm">CRM Automation Guide</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default WhyCRMDataIsBroken;
